/**
 * GlobalUserSearch — Feb 2026 (Slice 2 · Step 3 → v2: multi-source palette)
 *
 * A shell-level command palette for dispatchers / super-admins that searches
 * three sources in parallel:
 *
 *   USERS     server-side  /api/admin/users?q=…&limit=5
 *   ORDERS    client-side  filter over cached /api/orders (id prefix, customer)
 *   PRODUCTS  client-side  filter over cached /api/products (name)
 *
 * Triggers:
 *   • ⌘K / Ctrl+K from any /dispatch page
 *   • Tap the magnifier button in the breadcrumb strip
 *   • window.dispatchEvent(new Event('open-global-user-search'))
 *
 * Navigation contracts on pick:
 *   USER    → /dispatch/admin/users?openUser=<id>   (opens user drawer)
 *   ORDER   → /dispatch?orderId=<id>                (opens order detail dialog)
 *   PRODUCT → /dispatch/inventory?productId=<id>    (scrolls + highlights row)
 *
 * Kept the file name to avoid churn in imports; the "User" in the name is now
 * historical.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Search, X, ArrowRight, Loader2, User as UserIcon, Package, Box } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { prefetchOrders, prefetchProducts } from '../lib/prefetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const OPEN_EVENT = 'open-global-user-search';
const CACHE_MS = 30 * 1000; // 30s cache for orders + products
const PER_SECTION_LIMIT = 5;

export function openGlobalUserSearch() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function maskEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  return `${local.slice(0, 1)}${'*'.repeat(Math.max(1, local.length - 1))}@${domain}`;
}

const ROLE_ACCENT = {
  customer: '#39FF14',
  driver: '#00F0FF',
  dispatcher: '#FF9F0A',
  super_admin: '#B87BFF',
};

const STATUS_ACCENT = {
  pending: '#FF9F0A',
  confirmed: '#FFD60A',
  preparing: '#FFD60A',
  out_for_delivery: '#00F0FF',
  delivered: '#39FF14',
  cancelled: '#FF3B30',
  failed: '#FF3B30',
};

// Money formatter that survives strings, nulls, and NaN.
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function GlobalUserSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const isAuthorized = user && (user.role === 'dispatcher' || user.role === 'super_admin');
  const isDispatchScope = pathname.startsWith('/dispatch');
  const shouldMount = isAuthorized && isDispatchScope;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  // Cache: { orders: {ts, list}, products: {ts, list} }
  const cacheRef = useRef({ orders: null, products: null });

  // Combined flat list for keyboard nav. Rebuilt every render — cheap since
  // each section is capped at 5 items.
  const flat = [
    ...users.map((u) => ({ type: 'user', item: u })),
    ...orders.map((o) => ({ type: 'order', item: o })),
    ...products.map((p) => ({ type: 'product', item: p })),
  ];

  // ─── open triggers ────────────────────────────────────────────────────
  useEffect(() => {
    if (!shouldMount) return undefined;
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const meta = isMac ? e.metaKey : e.ctrlKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, [shouldMount]);

  // Reset on open/close.
  useEffect(() => {
    if (open) {
      setActiveIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
    setQ('');
    setUsers([]);
    setOrders([]);
    setProducts([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    return undefined;
  }, [open]);

  // Close on route change (result click sets pathname).
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  // ─── cached loaders ───────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    const cached = cacheRef.current.orders;
    if (cached && Date.now() - cached.ts < CACHE_MS) return cached.list;
    try {
      const { data } = await axios.get(`${API_URL}/api/orders`, { withCredentials: true });
      const list = Array.isArray(data) ? data : [];
      cacheRef.current.orders = { ts: Date.now(), list };
      return list;
    } catch {
      return cached?.list || [];
    }
  }, []);

  const loadProducts = useCallback(async () => {
    const cached = cacheRef.current.products;
    if (cached && Date.now() - cached.ts < CACHE_MS) return cached.list;
    try {
      const { data } = await axios.get(`${API_URL}/api/products`, { withCredentials: true });
      const list = Array.isArray(data) ? data : (data?.items || data?.products || []);
      cacheRef.current.products = { ts: Date.now(), list };
      return list;
    } catch {
      return cached?.list || [];
    }
  }, []);

  // ─── debounced multi-source search ────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    const term = q.trim();
    if (!term) {
      setUsers([]);
      setOrders([]);
      setProducts([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const termLc = term.toLowerCase();
        // Strip leading '#' so `#BBED5510` matches order id `BBED5510…`.
        const termOrderId = termLc.replace(/^#/, '');

        const [usersRes, ordersList, productsList] = await Promise.all([
          axios
            .get(`${API_URL}/api/admin/users`, {
              params: { q: term, limit: PER_SECTION_LIMIT },
              withCredentials: true,
              signal: ctrl.signal,
            })
            .then((r) => {
              const d = r.data;
              return Array.isArray(d) ? d : (d?.users || d?.items || []);
            })
            .catch(() => []),
          loadOrders(),
          loadProducts(),
        ]);

        // Client-side filter: order.id prefix, order.customer_name, order.customer_phone.
        const filteredOrders = ordersList
          .filter((o) => {
            const idLc = String(o.id || '').toLowerCase();
            const shortId = idLc.slice(0, 8);
            const nameLc = String(o.customer_name || '').toLowerCase();
            const phoneLc = String(o.customer_phone || '').toLowerCase();
            return (
              idLc.startsWith(termOrderId) ||
              shortId.includes(termOrderId) ||
              nameLc.includes(termLc) ||
              phoneLc.includes(termLc)
            );
          })
          .slice(0, PER_SECTION_LIMIT);

        // Client-side filter: product.name.
        const filteredProducts = productsList
          .filter((p) => String(p.name || '').toLowerCase().includes(termLc))
          .slice(0, PER_SECTION_LIMIT);

        setUsers(usersRes);
        setOrders(filteredOrders);
        setProducts(filteredProducts);
        setActiveIdx(0);
      } catch (e) {
        if (!axios.isCancel?.(e) && e.name !== 'CanceledError' && e.name !== 'AbortError') {
          console.debug('global search error:', e);
        }
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q, open, loadOrders, loadProducts]);

  // ─── selection / navigation ───────────────────────────────────────────
  const pick = useCallback(
    (entry) => {
      if (!entry) return;
      setOpen(false);
      if (entry.type === 'user' && entry.item?.id) {
        navigate(`/dispatch/admin/users?openUser=${entry.item.id}`);
      } else if (entry.type === 'order' && entry.item?.id) {
        navigate(`/dispatch?orderId=${entry.item.id}`);
      } else if (entry.type === 'product') {
        // Feb 2026 — deep-link polish. Pass `productId` so InventoryPage
        // smooth-scrolls to the row and briefly highlights it.
        const pid = entry.item?.id;
        navigate(pid ? `/dispatch/inventory?productId=${pid}` : '/dispatch/inventory');
      }
    },
    [navigate],
  );

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (!flat.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(flat[activeIdx]);
    }
  };

  if (!shouldMount) return null;
  if (!open) return null;

  const totalCount = flat.length;
  const emptyResults = !!q.trim() && !loading && totalCount === 0;
  // Running index across sections so keyboard nav aligns with rendered order.
  let idx = -1;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center p-3 sm:p-6 bg-[#050003]/85 backdrop-blur-md animate-in fade-in"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
      data-testid="global-user-search-overlay"
    >
      <div
        className="w-full max-w-lg mt-[8vh] bg-[#0C0612] border border-[#39FF14]/40 shadow-[0_0_40px_rgba(57,255,20,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input row */}
        <div className="flex items-center gap-2 border-b border-[#2A0F3D] px-3 py-2.5">
          <Search className="w-4 h-4 text-[#39FF14] shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search users · orders · products…"
            className="flex-1 bg-transparent border-0 outline-none text-[#EAE7E1] placeholder:text-[#4B6B52] font-mono text-sm py-1"
            data-testid="global-user-search-input"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <Loader2 className="w-4 h-4 text-[#4B6B52] animate-spin" />}
          <button
            onClick={() => setOpen(false)}
            className="p-1 text-[#4B6B52] hover:text-[#FF3B30] transition-colors"
            aria-label="Close search"
            data-testid="global-user-search-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Results */}
        <div
          className="max-h-[60vh] overflow-y-auto"
          data-testid="global-user-search-results"
        >
          {!q.trim() ? (
            <div className="p-4 text-center font-mono text-[10px] uppercase tracking-wider text-[#4B6B52] space-y-1">
              <p>Search customers, orders (id · customer · phone), or products.</p>
              <p className="text-[#39FF14]/60">↑↓ navigate · enter select · esc close</p>
            </div>
          ) : emptyResults ? (
            <div
              className="p-4 text-center font-mono text-[11px] uppercase tracking-wider text-[#4B6B52]"
              data-testid="global-user-search-empty"
            >
              Nothing matches “{q.trim()}”.
            </div>
          ) : (
            <div>
              {users.length > 0 && (
                <Section title="USERS" icon={UserIcon} data-testid="section-users">
                  {users.map((u) => {
                    idx += 1;
                    const localIdx = idx;
                    const isActive = localIdx === activeIdx;
                    const accent = ROLE_ACCENT[u.role] || '#EAE7E1';
                    const displayName = (u.name && u.name.trim()) || maskEmail(u.email) || u.email || 'Unnamed user';
                    const hasName = !!(u.name && u.name.trim());
                    return (
                      <Row
                        key={`u-${u.id}`}
                        active={isActive}
                        onClick={() => pick({ type: 'user', item: u })}
                        onHover={() => setActiveIdx(localIdx)}
                        testid={`global-user-search-result-${localIdx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="font-mono text-[10px] uppercase tracking-wider shrink-0"
                              style={{ color: accent }}
                            >
                              {(u.role || 'user').replace('_', ' ')}
                            </span>
                            <span className="text-[#EAE7E1] text-sm truncate">{displayName}</span>
                          </div>
                          {hasName && u.email && (
                            <div className="text-[#4B6B52] font-mono text-[10px] truncate mt-0.5">
                              {u.email}
                            </div>
                          )}
                        </div>
                      </Row>
                    );
                  })}
                </Section>
              )}
              {orders.length > 0 && (
                <Section title="ORDERS" icon={Package} data-testid="section-orders">
                  {orders.map((o) => {
                    idx += 1;
                    const localIdx = idx;
                    const isActive = localIdx === activeIdx;
                    const shortId = String(o.id || '').slice(0, 8).toUpperCase();
                    const accent = STATUS_ACCENT[o.status] || '#A3C7A8';
                    return (
                      <Row
                        key={`o-${o.id}`}
                        active={isActive}
                        onClick={() => pick({ type: 'order', item: o })}
                        onHover={() => setActiveIdx(localIdx)}
                        onPrewarm={() => prefetchOrders(queryClient)}
                        testid={`global-search-result-order-${localIdx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="font-mono text-[10px] uppercase tracking-wider shrink-0"
                              style={{ color: accent }}
                            >
                              {String(o.status || 'unknown').replace('_', ' ')}
                            </span>
                            <span className="text-[#EAE7E1] font-mono text-sm truncate">
                              #{shortId}
                            </span>
                            <span className="text-[#A3C7A8] text-sm truncate">
                              {o.customer_name || '—'}
                            </span>
                          </div>
                          <div className="text-[#4B6B52] font-mono text-[10px] truncate mt-0.5">
                            {money(o.total)} · {o.region || '—'}
                          </div>
                        </div>
                      </Row>
                    );
                  })}
                </Section>
              )}
              {products.length > 0 && (
                <Section title="PRODUCTS" icon={Box} data-testid="section-products">
                  {products.map((p) => {
                    idx += 1;
                    const localIdx = idx;
                    const isActive = localIdx === activeIdx;
                    const stock = Number(p.stock ?? p.inventory ?? 0);
                    const lowStock = stock <= (Number(p.low_stock_threshold) || 5);
                    return (
                      <Row
                        key={`p-${p.id}`}
                        active={isActive}
                        onClick={() => pick({ type: 'product', item: p })}
                        onHover={() => setActiveIdx(localIdx)}
                        onPrewarm={() => prefetchProducts(queryClient)}
                        testid={`global-search-result-product-${localIdx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-[10px] uppercase tracking-wider shrink-0 text-[#B87BFF]">
                              PRODUCT
                            </span>
                            <span className="text-[#EAE7E1] text-sm truncate">
                              {p.name || 'Unnamed'}
                            </span>
                          </div>
                          <div className="text-[#4B6B52] font-mono text-[10px] truncate mt-0.5">
                            {money(p.price)} ·{' '}
                            <span style={{ color: lowStock ? '#FF3B30' : '#4B6B52' }}>
                              stock {stock}
                            </span>
                          </div>
                        </div>
                      </Row>
                    );
                  })}
                </Section>
              )}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-[#2A0F3D] px-3 py-1.5 font-mono text-[9px] uppercase tracking-wider text-[#4B6B52] flex justify-between">
          <span>⌘K / CTRL+K to reopen</span>
          <span>
            {totalCount > 0 ? `${totalCount} result${totalCount === 1 ? '' : 's'}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── small helpers ────────────────────────────────────────────────────
function Section({ title, icon: Icon, children, ...rest }) {
  return (
    <div className="border-b border-[#1A0A2A] last:border-b-0" {...rest}>
      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1 font-mono text-[9px] uppercase tracking-widest text-[#4B6B52]">
        <Icon className="w-3 h-3" />
        <span>{title}</span>
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function Row({ active, onClick, onHover, onPrewarm, testid, children }) {
  // Feb 2026 — Fluidity Instant Triggers.
  // `onPointerDown` + `onTouchStart` warm the destination query the
  // millisecond a finger touches the row (before tap release), so by
  // the time the palette closes and the next page mounts, the cache is
  // populated and paint is one frame. Idempotent no-op if fresh.
  const warm = onPrewarm || (() => {});
  return (
    <li>
      <button
        onClick={onClick}
        onMouseEnter={onHover}
        onPointerDown={warm}
        onTouchStart={warm}
        onFocus={warm}
        className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${
          active
            ? 'bg-[#2A0F3D] border-l-[#39FF14]'
            : 'border-l-transparent hover:bg-[#1A0A2A]'
        }`}
        data-testid={testid}
      >
        {children}
        <ArrowRight
          className={`w-4 h-4 shrink-0 transition-opacity ${active ? 'opacity-100 text-[#39FF14]' : 'opacity-0'}`}
        />
      </button>
    </li>
  );
}

export default GlobalUserSearch;
